import { useNavigate } from "@solidjs/router";
import { createResource, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import type { ParkingResponse, PlaceSuggestion, PlaceView, Vehicle } from "../../shared/api";
import { ApiFailure, api } from "../api";
import { AppBar } from "../components/AppBar";
import { ComingSoonToast, comingSoon } from "../components/ComingSoon";
import { OperatedBy } from "../components/OperatedBy";
import { LoadingPill, ScreenLoader, Spinner } from "../components/Spinner";
import { directions } from "../directions";
import {
  computePlans,
  type ItineraryPlan,
  type Place,
  type PlanOrder,
  planTimes,
  priceDetail,
  sortPlans,
} from "../itinerary";
import { freshPosition, geo, watchPosition } from "../state/geo";
import { pricingPlans } from "../state/pricing";
import { session } from "../state/session";
import { tripPlan } from "../state/tripPlan";

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const MY_POSITION = "Ma position";

const MESSAGES: Record<string, string> = {
  SEARCH_UNAVAILABLE: "La recherche d'adresse est indisponible pour le moment.",
  RATE_LIMITED: "Trop de recherches. Réessayez dans un moment.",
};
const PLAN_ERRORS: Record<string, string> = {
  NO_BIKE: "Aucun vélo disponible près du départ pour le moment.",
  NO_PARKING: "Aucun stationnement près de l'arrivée.",
  NO_ROUTE: "Aucun itinéraire trouvé entre ces deux adresses.",
};

type Field = "from" | "to";
/** A chosen address, or the rider's own position (resolved when the routes are computed). */
type Endpoint = Place | "me";

const minutes = (sec: number) => Math.max(1, Math.ceil(sec / 60));

/**
 * Figma C1 "Recherche d'itinéraire", bike only: departure (default "Ma position") and arrival with
 * address autocomplete, swap, "Maintenant", the bike / walk tabs, the two orders and the results.
 * A result opens C1b (/itineraire). Signed-in riders only.
 */
export function Search() {
  const navigate = useNavigate();
  // Search and routes need an account: signed-out riders sign in first, then come back here.
  const [ready, setReady] = createSignal(false);
  onMount(async () => {
    if (await session.known()) setReady(true);
    else navigate(`/signin?next=${encodeURIComponent("/recherche")}`, { replace: true });
  });
  watchPosition();

  const [from, setFrom] = createSignal<Endpoint | undefined>("me");
  const [to, setTo] = createSignal<Endpoint | undefined>();
  const [editing, setEditing] = createSignal<Field | undefined>("to");
  const [order, setOrder] = createSignal<PlanOrder>("fastest");

  // One Mapbox search session (suggest… then retrieve) per visit.
  const sessionToken = crypto.randomUUID();
  const [query, setQuery] = createSignal("");
  const [suggestions, setSuggestions] = createSignal<PlaceSuggestion[]>([]);
  const [searching, setSearching] = createSignal(false);
  const [choosing, setChoosing] = createSignal<string>();
  const [error, setError] = createSignal<string>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let seq = 0;
  onCleanup(() => clearTimeout(timer));

  const fail = (e: unknown) =>
    setError(
      (e instanceof ApiFailure && (MESSAGES[e.code] ?? e.userMessage)) ||
        "La recherche n'a pas abouti. Réessayez.",
    );

  const nameOf = (e: Endpoint | undefined) => (e === "me" ? MY_POSITION : (e?.name ?? ""));
  const edit = (f: Field) => {
    seq++;
    clearTimeout(timer);
    setEditing(f);
    setQuery("");
    setSuggestions([]);
    setSearching(false);
    setError(undefined);
  };
  const setField = (f: Field, value: Endpoint | undefined) =>
    (f === "from" ? setFrom : setTo)(value);

  const onInput = (value: string) => {
    setQuery(value);
    setError(undefined);
    clearTimeout(timer);
    const q = value.trim();
    if (q.length < MIN_CHARS) {
      seq++;
      setSuggestions([]);
      setSearching(false);
      return;
    }
    timer = setTimeout(async () => {
      const mine = ++seq; // only the latest keystroke's answer is shown
      setSearching(true);
      const near = geo.located() ? `&near=${geo.position().lng},${geo.position().lat}` : "";
      try {
        const r = await api.get<{ suggestions: PlaceSuggestion[] }>(
          `/places/suggest?q=${encodeURIComponent(q)}&session=${sessionToken}${near}`,
        );
        if (mine === seq) setSuggestions(r.suggestions);
      } catch (e) {
        if (mine === seq) fail(e);
      } finally {
        if (mine === seq) setSearching(false);
      }
    }, DEBOUNCE_MS);
  };

  const choose = async (s: PlaceSuggestion) => {
    const f = editing();
    if (choosing() || !f) return;
    setChoosing(s.id);
    setError(undefined);
    try {
      const place = await api.get<PlaceView>(
        `/places/${encodeURIComponent(s.id)}?session=${sessionToken}`,
      );
      setField(f, { name: place.name, position: place.position });
      setEditing(undefined);
      setSuggestions([]);
      setQuery("");
    } catch (e) {
      fail(e);
    } finally {
      setChoosing(undefined);
    }
  };
  const useMyPosition = () => {
    const f = editing();
    if (!f) return;
    setField(f, "me");
    setEditing(undefined);
    setSuggestions([]);
  };
  const swap = () => {
    const a = from();
    setFrom(to());
    setTo(a);
  };

  // ---- Results: computed once both ends are known ---------------------------------------------
  const resolve = async (e: Endpoint): Promise<Place> =>
    e === "me"
      ? {
          name: MY_POSITION,
          position: geo.located() ? geo.position() : await freshPosition(4_000),
        }
      : e;
  const [results] = createResource(
    () => {
      const a = from();
      const b = to();
      return ready() && a && b && !editing() ? { a, b } : undefined;
    },
    async ({ a, b }) => {
      const [start, end, vehicles, parking] = await Promise.all([
        resolve(a),
        resolve(b),
        api.get<Vehicle[]>("/vehicles"),
        api.get<ParkingResponse>("/parking"),
      ]);
      return computePlans({
        from: start,
        to: end,
        vehicles,
        zones: parking.parking,
        route: directions.get,
      });
    },
  );
  const plans = () =>
    results.state === "ready" ? sortPlans(results().plans, order()) : ([] as ItineraryPlan[]);
  const resultError = () => {
    const e = results.error as unknown;
    if (!e) return undefined;
    const code = e instanceof ApiFailure ? e.code : e instanceof Error ? e.message : "";
    return PLAN_ERRORS[code] ?? "Itinéraire indisponible pour le moment. Réessayez.";
  };
  const open = (p: ItineraryPlan) => {
    tripPlan.choose(p);
    navigate("/itineraire");
  };

  const label = "w-[86px] shrink-0 text-sm font-medium leading-[1.25] tracking-[0.14px] text-text";
  const fieldRow = (f: Field) => (
    <div class="flex h-[46px] items-center">
      <span class={label}>{f === "from" ? "Départ :" : "Arrivée :"}</span>
      <Show
        when={editing() === f}
        fallback={
          <button
            type="button"
            class="h-full min-w-0 flex-1 border-b border-text text-left text-sm font-medium leading-[1.25] tracking-[0.14px] text-text"
            onClick={() => edit(f)}
          >
            <span class="block truncate">
              {nameOf(f === "from" ? from() : to()) || (
                <span class="font-normal opacity-70">Adresse, lieu…</span>
              )}
            </span>
          </button>
        }
      >
        <label class="flex h-full min-w-0 flex-1 items-center gap-2 border-b border-violet">
          <input
            type="search"
            class="min-w-0 flex-1 bg-transparent text-base text-strong outline-none placeholder:text-text"
            placeholder="Adresse, lieu…"
            value={query()}
            onInput={(e) => onInput(e.currentTarget.value)}
            autocomplete="off"
            enterkeyhint="search"
            aria-label={f === "from" ? "Départ" : "Arrivée"}
            ref={(el) => queueMicrotask(() => el.focus())}
          />
          <Show when={searching()}>
            <Spinner size={18} class="text-violet" />
          </Show>
        </label>
      </Show>
    </div>
  );

  return (
    <main class="screen bg-surface">
      <AppBar title="Recherche d'itinéraire" back="/" />
      <Show when={ready()} fallback={<ScreenLoader />}>
        <div class="screen-body px-[15px] pt-[18px] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          {/* Figma C1 form: two fields, the swap disc on their right. */}
          <div class="flex items-center gap-3">
            <div class="min-w-0 flex-1">
              {fieldRow("from")}
              {fieldRow("to")}
            </div>
            <button
              type="button"
              class="h-10 w-10 shrink-0 rounded-full bg-violet flex items-center justify-center active:brightness-95"
              onClick={swap}
              aria-label="Inverser départ et arrivée"
            >
              <img src="/icons/swap-vert-white-24.svg" width="24" height="24" alt="" />
            </button>
          </div>

          <Show
            when={editing()}
            fallback={
              <>
                {/* Only "now" for the MVP: the time picker is not wired. */}
                <div class="mt-[10px] flex items-center text-sm font-medium leading-[1.25] tracking-[0.14px]">
                  <span class="text-text">Départ :</span>
                  <img
                    src="/icons/chevron-down-20.svg"
                    width="20"
                    height="20"
                    alt=""
                    class="ml-[3px]"
                  />
                  <span class="ml-[9px] text-[#ee7c11]">Maintenant</span>
                </div>

                <Show when={from() && to()}>
                  {/* Mode tabs: bike selected; tram needs a transit source (not wired). */}
                  <div class="mt-6 grid grid-cols-4 text-center">
                    <ModeTab icon="/icons/tram-violet-30.svg" label="Tram" onClick={comingSoon} />
                    <ModeTab
                      icon="/icons/bike-indigo-30.svg"
                      label="Vélo"
                      selected
                      value={
                        plans()[0]
                          ? `${planTimes(Date.now(), plans()[0] as ItineraryPlan).totalMin}min`
                          : "--"
                      }
                    />
                    <ModeTab
                      icon="/icons/walk-violet-30.svg"
                      label="À pied"
                      value={
                        results.state === "ready" && results().walkOnlySec !== undefined
                          ? `${minutes(results().walkOnlySec as number)}min`
                          : "--"
                      }
                    />
                    <ModeTab label="P+R" onClick={comingSoon} />
                  </div>

                  <div class="mt-[18px] flex justify-center gap-[10px] text-sm font-medium leading-[1.25] tracking-[0.14px] text-text">
                    <OrderButton
                      on={order() === "fastest"}
                      onClick={() => setOrder("fastest")}
                      label="Le plus rapide"
                    />
                    <OrderButton
                      on={order() === "leastWalk"}
                      onClick={() => setOrder("leastWalk")}
                      label="Le moins de marche à pied"
                    />
                  </div>

                  <Show when={results.loading}>
                    <div class="mt-6 flex justify-center">
                      <LoadingPill label="Calcul des itinéraires…" />
                    </div>
                  </Show>
                  <Show when={!results.loading && resultError()}>
                    {(m) => (
                      <p class="mt-6 px-1 text-sm text-[#e53935]" role="alert">
                        {m()}
                      </p>
                    )}
                  </Show>
                  <div class="mt-[6px] flex flex-col gap-[10px] px-1">
                    <For each={results.loading ? [] : plans()}>
                      {(p) => <ResultCard plan={p} onOpen={() => open(p)} />}
                    </For>
                  </div>
                </Show>
              </>
            }
          >
            <Show when={error()}>
              {(m) => (
                <p class="mt-4 text-sm text-[#e53935]" role="alert">
                  {m()}
                </p>
              )}
            </Show>
            <ul class="mt-2" aria-label="Suggestions">
              <Show when={(editing() === "from" ? to() : from()) !== "me"}>
                <li>
                  <button
                    type="button"
                    class="w-full flex items-center gap-3 py-3 border-b border-border text-left"
                    onClick={useMyPosition}
                  >
                    <span class="h-9 w-9 shrink-0 rounded-full bg-surface2 flex items-center justify-center">
                      <img src="/icons/locate.svg" width="20" height="20" alt="" />
                    </span>
                    <span class="text-[15px] font-medium text-strong">{MY_POSITION}</span>
                  </button>
                </li>
              </Show>
              <For each={suggestions()}>
                {(s) => (
                  <li>
                    <button
                      type="button"
                      class="w-full flex items-center gap-3 py-3 border-b border-border text-left"
                      onClick={() => void choose(s)}
                      disabled={!!choosing()}
                    >
                      <span class="h-9 w-9 shrink-0 rounded-full bg-surface2 flex items-center justify-center">
                        <Show when={choosing() === s.id} fallback={<PinIcon />}>
                          <Spinner size={18} class="text-violet" />
                        </Show>
                      </span>
                      <span class="min-w-0">
                        <span class="block truncate text-[15px] font-medium text-strong">
                          {s.name}
                        </span>
                        <Show when={s.address}>
                          <span class="block truncate text-[13px] text-text">{s.address}</span>
                        </Show>
                      </span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
            <Show
              when={
                !searching() &&
                query().trim().length >= MIN_CHARS &&
                suggestions().length === 0 &&
                !error()
              }
            >
              <p class="mt-4 text-sm text-text">Aucun résultat.</p>
            </Show>
            <Show when={(editing() === "from" ? from() : to()) !== undefined}>
              <button
                type="button"
                class="mt-4 self-start text-sm text-link underline"
                onClick={() => setEditing(undefined)}
              >
                Annuler
              </button>
            </Show>
          </Show>
          <OperatedBy class="mt-auto pt-6" />
        </div>
      </Show>
      <ComingSoonToast />
    </main>
  );
}

/** Figma C1 mode tab: 30 px icon, the orange bar under the selected one, its duration. */
function ModeTab(props: {
  icon?: string;
  label: string;
  value?: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      class="flex flex-col items-center"
      aria-label={props.label}
      aria-pressed={!!props.selected}
      onClick={() => props.onClick?.()}
    >
      <span class="flex h-[30px] items-center">
        <Show
          when={props.icon}
          fallback={
            <span class="h-[18px] w-9 rounded-[1px] bg-violet text-center text-xs font-bold leading-[18px] text-white">
              P+R
            </span>
          }
        >
          <img src={props.icon} width="30" height="30" alt="" />
        </Show>
      </span>
      <span
        class={`mt-[6px] h-[3px] w-7 rounded-[1px] ${props.selected ? "bg-[#ee7c11]" : "bg-transparent"}`}
      />
      <span class="mt-[7px] text-[11px] leading-[1.25] tracking-[0.11px] text-text">
        {props.value ?? "--"}
      </span>
    </button>
  );
}

function OrderButton(props: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      class="flex h-10 items-center gap-1"
      aria-pressed={props.on}
      onClick={props.onClick}
    >
      <img
        src={props.on ? "/icons/check-circle-green-20.svg" : "/icons/check-circle-grey-20.svg"}
        width="20"
        height="20"
        alt=""
      />
      {props.label}
    </button>
  );
}

/** Figma C1 result card: walk › bike › walk chips, times, price, total minutes. */
function ResultCard(props: { plan: ItineraryPlan; onOpen: () => void }) {
  const times = () => planTimes(Date.now(), props.plan);
  const price = () => {
    const id = props.plan.bike.pricingPlanId;
    const plan = id ? pricingPlans()[id] : undefined;
    return plan ? priceDetail(plan, props.plan.ride.sec).total : undefined;
  };
  const chip = (icon: string, n: number) => (
    <span class="flex items-end">
      <img src={icon} width="20" height="20" alt="" />
      <span class="text-[9px] leading-[1.25] tracking-[0.09px] text-text">{n}</span>
    </span>
  );
  return (
    <button
      type="button"
      class="flex h-[68px] w-full items-center rounded-[4px] bg-[#f7f2fa] pl-3 text-left shadow-[0_1px_3px_rgba(0,0,0,0.16)] active:brightness-97"
      onClick={props.onOpen}
    >
      <span class="min-w-0 flex-1">
        <span class="flex items-center gap-[2px]">
          {chip("/icons/walk-violet-20.svg", minutes(props.plan.walk.sec))}
          <img src="/icons/chevron-right-violet-18.svg" width="18" height="18" alt="" />
          {chip("/icons/bike-indigo-20b.svg", minutes(props.plan.ride.sec))}
          <span class="ml-[2px] flex h-5 w-5 items-center justify-center rounded-full border-[1.25px] border-white bg-indigo shadow-[0_0.6px_0.9px_rgba(13,8,51,0.3)]">
            <img src="/icons/bike-white-12.svg" width="12.5" height="12.5" alt="" />
          </span>
          <img src="/icons/chevron-right-violet-18.svg" width="18" height="18" alt="" />
          {chip("/icons/walk-violet-20.svg", minutes(props.plan.last.sec))}
        </span>
        <span class="mt-[6px] flex items-center whitespace-pre text-[10px] leading-[1.25] tracking-[0.1px] text-text">
          {`Départ :${times().departure}  -  Arrivée :${times().arrival}`}
          <Show when={price()}>
            {(p) => (
              <>
                <img
                  src="/icons/realtime-green-13.svg"
                  width="13"
                  height="13"
                  alt=""
                  class="ml-[10px]"
                />
                <span class="ml-[9px] text-[10.5px] font-medium tracking-[0.105px] text-violet">
                  Vélo Pony {p()}
                </span>
              </>
            )}
          </Show>
        </span>
      </span>
      <span class="w-[54px] shrink-0 text-center font-medium leading-[1.25] text-text">
        <span class="block text-xl tracking-[0.2px]">{times().totalMin}</span>
        <span class="block text-[13px] tracking-[0.13px]">min</span>
      </span>
    </button>
  );
}

function PinIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"
        fill="#6D3E91"
      />
    </svg>
  );
}
